import angular from "angular";

import {
  ContactType, EmploymentLogic, Dates, Taxcards, EmploymentRelationTaxcard, MessageThreadStatus, MessageFrom,
  ThreadedMessageType, Arrays, Numeric, PayrollDetails, Payrolls4, PayrollListItem, CalculationListItem, Calculation,
  Calculations, PayrollStatus, SalaryDateKind, SalaryDateLogic, Translations, Employment, Employments, InvoicesLogic,
  ApiListItemValidation, CalculationStatus, InputEnumOption, HolidayCode, AbsenceCauseCode, Objects, WorktimeImport,
  EmploymentRelationType, WorktimeImportRow, IApiListItem, ODataResult
} from "@salaxy/core";

import { UiHelpers, UiCrudHelpers, InvoicesService, UserSettingsService, SessionService } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { MessageThreadCrudController } from "../communications/MessageThreadCrudController";
import { TabButtonsController } from "../../controllers/helpers/TabButtonsController";
/**
 * Payroll header change info.
 */
class HeaderChangeInfo {

  /** The payment channel has been changed. */
  public isPaymentChannelChanged: boolean;
  /** The period has been changed. */
  public isPeriodChanged: boolean;
  /** Salary date or salary date kind has been changed */
  public isSalaryDateChanged: boolean;
}

/**
 * v4 version of the Payrolls CRUD Controller.
 */
export class Payroll4CrudController extends ApiCrudObjectController<PayrollDetails, PayrollListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Payrolls4", "UiHelpers", "$location", "$routeParams", "Calculations", "Employments", "InvoicesService", "$timeout", "UserSettingsService", "SessionService", "Taxcards", "UiCrudHelpers", "$rootScope", "$scope"];

  /** Current calculation ID if one is selected. */
  public calcId: string | undefined;

  /** Current calculation if on is selected and loaded. */
  public calc: Calculation | undefined;

  /** Original calculation */
  public originalCalc: Calculation | undefined;

  /** Current calc list item*/
  public calcListItem: CalculationListItem | undefined;

  /** Active tab for single calculation. */
  public calcTab = "rows";

  /** Flags to indicate if the request is in progress */
  public loaders: any = {};

  /** Flag for showing the header dialog */
  public showHeader = false;

  /** Calculations for the reports. */
  public reportCalcs: CalculationListItem[] = [];

  /** Tab buttons component controller, to programmatically change tabs. */
  private tabButtonsCtrl: TabButtonsController;

  /**
   * Action buttons that are visible in the user interface.
   * Default is "copy", "cancel". If read only is forced neither is included.
   * TODO: Current implementation is for read-only view only.
   */
  public actions: ("none" | "copy" | "pay" | "cancel")[];

  /** order field */
  public orderField: string = undefined;
  /** order direction */
  public orderReverse: boolean = false;

  /** Page size */
  public pageSize = 20;

  /** List of employments data for the calcs. */
  public employments: { [key: string]: IApiListItem };

  /** Page size options */
  public pageSizeOptions = { "10": "10", "20": "20", "30": "30", "40": "40", "50": "50" };

  /** AI assistant  */
  private _openAiKey: string = null;

  /** Column filter choices that affect the backend query. */
  private columnFilters = {
    "PartnerMessageClosed": "",
    "PartnerMessageIssue": ""
  };

  constructor(
    private payrollsApi: Payrolls4,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private calcsApi: Calculations,
    private employmentsApi: Employments,
    private invoicesService: InvoicesService,
    private $timeout: angular.ITimeoutService,
    private userSettingsService: UserSettingsService,
    private sessionService: SessionService,
    private taxcardsApi: Taxcards,
    private uiCrudHelpers: UiCrudHelpers,
    private $rootScope: angular.IRootScopeService,
    private $scope: angular.IScope,
  ) {
    // Dependency injection
    super(payrollsApi, uiHelpers, $location, $routeParams);
    const handler = this.$rootScope.$on("payroll-tab-event", (_event: any, tabToSelect: string) => {
      this.selectTab(tabToSelect);
    });
    this.$scope.$on("$destroy", handler);
  }

  /**
   * Initialization code.
   */
  public $onInit() {
    super.$onInit();

    this.$scope.$on("tabButtonsControllerReady", (event, data: { ctrl: TabButtonsController, tabsId: string }) => {
      if (data.tabsId === "payrollDetails") {
        this.tabButtonsCtrl = data.ctrl;
        if (this.isAiDemoEditable) {
          this.selectTab("rows");
        }
        else if (this.isReadOnlyForced) {
          this.selectTab("reports");
        }
      }
    });
  }

  /** Catched the return of reload to update values. */
  public reload(): Promise<PayrollDetails> {
    return super.reload().then((payroll) => {
      if (super.isNew()) {
        if (payroll?.input) {
          payroll.input.paymentChannel = this.invoicesService.defaultChannel;
        }
      }
      return payroll;
    });
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/payroll",
      detailsUrl: this.detailsUrl || "/payroll/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Payrolls.html",
      oDataOptions: {},
    };
  }

  /** Page size */
  public get pageSizeOption() {
    return `${this.pageSize}`;
  }

  /** Page size */
  public set pageSizeOption(value: string) {
    this.pageSize = Number.parseInt(value);
  }

  /** Check assistant key */
  public get hasAssistantKey(): boolean {
    this._openAiKey = this._openAiKey || window.localStorage.getItem("openAiKey") || "";
    return !!this._openAiKey;
  }

  /** Returns true for AI Demo approval case */
  public get isAiDemoWaitingApproval(): boolean {
    return this.sessionService.isInRole("aidemo") && !this.sessionService.isInRole("pro") && this.current?.info?.status === PayrollStatus.WaitingApproval;
  }

    /** Returns true for AI Demo approval case */
  public get isAiDemoEditable(): boolean {
    return this.sessionService.isInRole("aidemo") && !this.sessionService.isInRole("pro") && !this.current?.isReadOnly;
  }

  /** Shows dummy dialog for AI demo approval */
  public showAiDemoApproval() {
    this.uiHelpers.showConfirm("Palkkalistan hyväksyntä", "Haluatko hyväksyä tämän palkkalistan ja siirtää palkkalistan maksettavaksi?", "Hyväksy", "Peruuta");
  }

  /**
   * Selects and shows a tab in the payroll details view
   */
  public selectTab(tab: string): void {
    this.tabButtonsCtrl.showTab(tab);
  }

  /**
   * Returns true, if the given action button should be shown
   * @param action One of the action values: See actions field type for supported values.
   */
  public showAction(action: string): boolean {
    const actions = this.actions?.length ? this.actions :
      this.isReadOnlyForced ? [] : ["copy", "cancel"];

    if (action === "pay") {
      // Check action and status
      return !!actions.find(x => x == action) &&
        (!!this.current &&
          (this.current.info.status === PayrollStatus.Draft ||
            this.current.info.status === PayrollStatus.WaitingApproval
          )
        );
    }

    return !!actions.find(x => x == action);
  }

  /**
   * If true, the form controls should be read-only (no control at all).
   */
  public get isReadOnly() {
    return super.isReadOnly || this.current?.info?.status === PayrollStatus.PaymentStarted;
  }

  /** Returns true if the period of a calculation is different than the period of the Payroll. */
  public isDateDifferent(calc: CalculationListItem): boolean {
    if (!calc?.data) {
      return false;
    }
    return Dates.asDate(calc.data.workStartDate) !== Dates.asDate(this.current.input.period.start)
      || Dates.asDate(calc.data.workEndDate) !== Dates.asDate(this.current.input.period.end);
  }

  /** Save changes to the payroll as well as to changed calculations */
  public save(options: { applyEmploymentDefaultRows: boolean } = null): Promise<PayrollDetails> {
    this.loaders[this.current?.id || "new"] = true;
    if (this.isNew() && this.current?.input?.calculations?.length > 0) {
      // Create new payroll
      const calcIds = this.current.input.calculations.slice();
      this.current.input.calculations = [];
      return this.calcsApi.getSingle(calcIds[0]).then((calc) => {
        return this.payrollsApi.getCalcsOData(calc.info.payrollId, null).then((odataResult) => {
          return super.save(options).then(() => {
            const calcs = this.orderCalcs(calcIds, odataResult.items);
            const data: any = {};
            data.listItemType = "CalculationListItem";
            const columns = undefined;
            const paramsTemplate = undefined;
            const action = (items /*, data */) => {
              const itemIds = items.map((x) => x.id);
              const removeAllRows = options?.applyEmploymentDefaultRows;
              return this.payrollsApi.saveCalculations(this.current.id, itemIds, false, options?.applyEmploymentDefaultRows, removeAllRows).then((result) => {
                const results = items.map(() => ({ message: "", result: [] }));
                if (results.length > 0) {
                  results[0].result = result;
                }
                return results;
              });
            };
            return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.UI_Terms.wait"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then(() => {
              return this.$timeout(() => this.reload().then(() => {
                this.$location.path("/payroll/details/" + this.current.id + "&refresh=" + new Date().getTime());
                return this.current;
              }), 5000); // HACK: wait for index
            });
          });
        });
      });
    }
    else {
      const wasNew = this.isNew();
      const headerChangeInfo = this.getHeaderChangeInfo();
      return super.save(options).then((result) => {
        if (wasNew) {
          delete this.loaders["new"];
          delete this.loaders[this.current.id];
          return result;
        }
        else {
          if (headerChangeInfo.isPaymentChannelChanged || headerChangeInfo.isSalaryDateChanged || headerChangeInfo.isPeriodChanged) {
            const shouldChangePeriod = () => {
              if (headerChangeInfo.isPeriodChanged) {
                return this.uiHelpers.showConfirm("SALAXY.NG1.PayrollComponent.showDateRangeDialogTitle",
                  "SALAXY.NG1.PayrollComponent.showDateRangeDialogDescr",
                  "SALAXY.UI_Terms.yes", "SALAXY.UI_Terms.no");
              }
              else {
                return Promise.resolve(false);
              }
            };
            return shouldChangePeriod().then((doChangePeriod) => {
              if (!doChangePeriod && !headerChangeInfo.isPaymentChannelChanged && !headerChangeInfo.isSalaryDateChanged) {
                delete this.loaders["new"];
                delete this.loaders[this.current.id];
                return result;
              }
              else {
                const calcs = this.calcs.slice(0);
                const data: any = {};
                data.listItemType = "CalculationListItem";
                const columns = undefined;
                const paramsTemplate = undefined;
                const action = (items /*, data */) => {
                  const itemIds = items.map((x) => x.id);
                  return this.calcsApi.getMulti(itemIds).then((apiCalcs) => {
                    for (const apiCalc of apiCalcs) {
                      if (doChangePeriod) {
                        apiCalc.info.workStartDate = this.current.input.period.start;
                        apiCalc.info.workEndDate = this.current.input.period.end;
                        apiCalc.framework.numberOfDays = this.current.input.period.daysCount;
                      }
                    }
                    return this.payrollsApi.saveCalculations(this.current.id, apiCalcs).then((calcsResult) => {
                      const results = items.map(() => ({ message: "", result: [] }));
                      if (results.length > 0) {
                        results[0].result = calcsResult;
                      }
                      return results;
                    });
                  });
                };
                return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.commitAllCalcs.heading"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
                  if (dialogResult.done) {
                    dialogResult.results.forEach((result) => {
                      for (const item of result.result) {
                        const listItem = this.calcs.find((c) => c.id === item.id);
                        if (listItem) {
                          angular.copy(item, listItem);
                        }
                        else {
                          this.calcs.push(item);
                        }
                      }
                    });
                    this.setPageItems();
                  }
                  delete this.loaders["new"];
                  delete this.loaders[this.current.id];
                  return this.reload();
                });
              }
            });
          }
          else {
            delete this.loaders["new"];
            delete this.loaders[this.current.id];
            return result;
          }
        }
      });
    }
  }

  /** Recalculate and save worktimes of all calculations. */
  public saveWorktimes(): Promise<void> {
    this.loaders["worktime" + this.current?.id] = true;
    const calcs = this.calcs.slice(0);
    const data: any = {};
    data.listItemType = "CalculationListItem";
    const columns = undefined;
    const paramsTemplate = undefined;
    const action = (items) => {
      const itemIds = items.map((x) => x.id);
      return this.payrollsApi.saveCalculations(this.current.id, itemIds, true).then((result) => {
        const results = items.map(() => ({ message: "", result: [] }));
        if (results.length > 0) {
          results[0].result = result;
        }
        return results;
      });
    };
    return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.commitAllCalcs.heading"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
      if (dialogResult.done) {
        dialogResult.results.forEach((result) => {
          for (const item of result.result) {
            const listItem = this.calcs.find((c) => c.id === item.id);
            if (listItem) {
              angular.copy(item, listItem);
            }
          }
        });
        const currentPage = this.page;
        // start reload calculations
        currentPage.calcs = [];
        currentPage.originalCalcs = [];
        // and payroll
        return this.reload();
      }
    }).then(() => {
      delete this.loaders["worktime" + this.current?.id];
    });
  }

  /**
   * Calculates the total amount of days for the given period type.
   * @param calc Calculation to calculate days for.
   * @param periodType Period type to calculate days for.
   * @returns Sum of days for the given period type.
   */
  public calculatePeriodDays(calc: Calculation, periodType: "absencesDays" | "leavesDays" | "paidAbsencesDays" | "accruedDays") {
    switch (periodType) {
      case "leavesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode == AbsenceCauseCode.AnnualLeave) ?? [], (x) => x.period.daysCount);
      case "absencesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && !x.isPaid) ?? [], (x) => x.period.daysCount);
      case "paidAbsencesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && x.isPaid) ?? [], (x) => x.period.daysCount);
      case "accruedDays":
        if (this.showAccruals(calc)) {
          return Arrays.sum(calc.worktime?.accruals, (x) => x.daysAccrued);
        }
        return 0;
    }
  }

  /**
   * Returns true if the accruals are accumulated.
   * @param calc Calculation.
   * @returns Returns true if the accruals are accumulated.
   */
  public showAccruals(calc: Calculation) {
    return calc.worktime?.accrualSpec?.code == HolidayCode.Permanent14Days || calc.worktime?.accrualSpec?.code == HolidayCode.Permanent35Hours || calc.worktime?.accrualSpec?.code == HolidayCode.TemporaryTimeOff || calc.worktime?.accrualSpec?.code == HolidayCode.Other || calc.worktime?.accrualSpec?.code == HolidayCode.Script;
  }

  /**
   * Creates a copy of a payroll
   * This is a synchronous method that should basically convert a saved item to a new item.
   * @param copySource Item (container item) to copy as new.
   */
  public copyItem(copySource: PayrollDetails): PayrollDetails {
    const oldId = copySource.id;
    const copy = super.copyItem(copySource);
    copy.input.salaryDate = null;
    copy.info.salaryDate = null;
    copy.info.messageThreadId = null;
    copy.info.originalPaymentId = undefined;
    copy.info.paymentId = undefined;
    if (copy.info.status === PayrollStatus.Template) {
      copy.input.template = oldId;
    }
    copy.info.status = PayrollStatus.Draft;
    // HACK: remove payroll-service flag. This will be replaced with invoicing flags eventually.
    if (copy.usecase &&
      (copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/pro-calc" ||
        copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/calc")) {
      copy.usecase = {};
    }
    return copy;
  }

  /** Close the header dialog */
  public closeHeader() {
    delete this.showHeader;
    if (this.isNew()) {
      this.$location.path(this.getDefaults().listUrl);
    }
  }

  /**
   * Sets the current calculation ID
   * @param calcId The ID of the calculation to set.
   * Set as undefined to hide the current calculation.
   */
  public setCalc(calcId: string | undefined) {
    this.calcId = calcId;
    this.calcListItem = this.calcs.find((c) => c.id === calcId);
    this.calc = undefined;
    this.originalCalc = undefined;
    if (calcId) {
      const loadedCalc = this.page.calcs.find((c) => c.id === calcId);
      if (loadedCalc && !!loadedCalc.rows) {
        // this is needed for refresh the calc page
        this.uiHelpers.wait(0).then(() => {
          this.calc = loadedCalc;
          this.originalCalc = this.page.originalCalcs.find((c) => c.id === calcId);
        });
      }
      else {
        this.calcsApi.getSingle(calcId).then((result) => {
          // find from current page
          const preCalc = this.page.calcs.find((c) => c.id === calcId);
          if (preCalc) {
            const calc = result;
            angular.copy(calc, preCalc);
            const originalCalc = this.page.originalCalcs.find((c) => c.id === calcId);
            angular.copy(calc, originalCalc);
            this.calc = preCalc;
            this.originalCalc = originalCalc;
          }
          else {
            // find from all pages
            const page = this._pages.pages.find((p) => p.items.find((c) => c.id === calcId));
            if (page) {
              const calc = result;
              page.calcs.push(calc);
              const originalCalc = angular.copy(calc);
              page.originalCalcs.push(originalCalc);
              this.calc = calc;
              this.originalCalc = originalCalc;
              this._pages.currentIdx = page.current - 1;
            }
          }
        });
      }
    }
  }

  private _canvasInfo = {
    calc: false,
    header: false
  };
  /**
   * Returns true if the canvas should be shown.
   */
  public shouldShowCanvas(canvas: "calc" | "header", truly: any): boolean {
    const showCanvas = !!truly;
    const launcher = document.querySelector("iframe#launcher") as HTMLElement;
    if (launcher) {
      this._canvasInfo[canvas] = showCanvas;
      const hideHelp = this._canvasInfo["calc"] || this._canvasInfo["header"];
      if (hideHelp) {
        launcher.style.display = "none";
      }
      else {
        launcher.style.display = "";
      }
    }
    return showCanvas;
  }

  /**
   * Advances to next calc.
   */
  public goToCalc(previous = false) {
    let idx = this.calcs.indexOf(this.calcListItem);
    if (idx == -1) {
      return;
    }


    const saveIfChanged = () => {
      if (this.hasEmploymentChange) {
        return this.saveEmployment((this as any).$employment.current, false);
      }
      else if (this.hasCalcChange) {
        return this.saveCalc(this.calc);
      }
      else {
        return Promise.resolve(this.current);
      }
    };

    return saveIfChanged().then(() => {
      if (previous) {
        idx -= 1;
        return idx > -1 ? this.setCalc(this.calcs[idx].id) : this.setCalc(this.calcs[this.calcs.length - 1].id);
      }
      else {
        idx += 1;
        return idx < this.calcs.length ? this.setCalc(this.calcs[idx].id) : this.setCalc(this.calcs[0].id);
      }
    });
  }

  /**
   * Checks if the current calcs has changes.
   */
  public get hasCalcChange(): boolean {
    if (this.calc?.isReadOnly) {
      return false;
    }
    return !angular.equals(this.calc, this.originalCalc);
  }

  /**
   * Checks if the current employment has changes.
   */
  public get hasEmploymentChange(): boolean {
    if (this.calc?.isReadOnly) {
      return false;
    }
    const employment = (this as any).$employment;

    if (employment?.current && employment?.original) {
      // HACK fixes
      // remove $$hashKey added by angular
      const current = angular.fromJson(angular.toJson(employment.current));
      // add defaults to original as in current
      const original = angular.copy(employment.original);
      EmploymentLogic.getContact(original, ContactType.EmployerOverride);
      return !angular.equals(current, original);
    }
    return false;
  }

  /**
   * Toggles the current calculation ID
   * @param calcId The ID of the calculation to set.
   */
  public toggleCalc(calcId: string | undefined) {
    if (this.calcId === calcId) {
      this.setCalc(undefined);
    } else {
      this.setCalc(calcId);
    }
  }

  /**
   * Saves the calculation and reloads the payroll.
   */
  public saveCalc(calcToSave: Calculation, applyWorktime = false, applyEmploymentDefaultRows = false): Promise<PayrollDetails> {
    const calcId = calcToSave.id;

    this.loaders[calcId] = true;
    return this.payrollsApi.saveCalculations(this.current.id, [calcToSave], applyWorktime, applyEmploymentDefaultRows).then((items) => {
      for (const item of items) {
        const listItem = this.calcs.find((c) => c.id === item.id);
        if (listItem) {
          angular.copy(item, listItem);
        }
      }
      delete this.loaders[calcId];
      return this.calcsApi.getSingle(calcId).then((calc) => {
        if (this.calcId == calcId) {
          if (this.calc) {
            angular.copy(calc, this.calc);
            angular.copy(calc, this.originalCalc);
          }
          else {
            this.calc = calc;
            this.originalCalc = angular.copy(calc);
          }
        }
        else {
          // start reload calculations
          const currentPage = this.page;
          currentPage.calcs = currentPage.calcs.filter((x) => x.id != calcId);
          currentPage.originalCalcs = currentPage.originalCalcs.filter((x) => x.id != calcId);
        }
        return this.reload();
      });
    });
  }

  /**
   * Refreshes the calculation by removing the specified calculation from the current page's
   * calculations and original calculations, then reloads the data.
   */
  public refreshCalc(calc: Calculation): angular.IPromise<PayrollDetails> {
    const currentPage = this.page;
    currentPage.calcs = currentPage.calcs.filter((x) => x.id != calc.id);
    currentPage.originalCalcs = currentPage.originalCalcs.filter((x) => x.id != calc.id);
    return this.$timeout(() => {
      return this.calcsApi.getOData({$filter: `id eq '${calc.id}'`}).then(result => {
        const item = result.items[0];
        const listItem = this.calcs.find((c) => c.id === item.id);
        if (listItem) {
          angular.copy(item, listItem);
        }
        return this.current;
      });
    }, 5000);
  }

  /**
   * Refreshes the calculation after a workflow event by:
   * 1. Fetching the latest version of the calculation from the API
   * 2. Updating the local calc and originalCalc copies
   * 3. Triggering a refresh of the calculation in the list
   * @returns Promise that resolves with the updated PayrollDetails
   */
  public refreshAfterWorkflow(): Promise<PayrollDetails> {
    return this.calcsApi.getSingle(this.calc.id).then((calc) => {
      if (this.calc) {
        angular.copy(calc, this.calc);
        angular.copy(calc, this.originalCalc);
      } else {
        this.calc = calc;
        this.originalCalc = angular.copy(calc);
      }
      return this.refreshCalc(this.calc);
    });
  }

  /**
   * Adds salary defaults to the current calculation by saving it with the addSalaryDefaults flag.
   * @returns Promise that resolves with the updated PayrollDetails after saving
   */
  public async addSalaryDefaultsToCalc(): Promise<PayrollDetails> {
    this.loaders[this.calc.id + "-add-salary-defaults"] = true;
    return this.saveCalc(this.calc, false, true).then(() => {
      delete this.loaders[this.calc.id + "-add-salary-defaults"];
      return this.current;
    });
  }

  /** Saves the employment and updates the calcs rows optionally */
  public saveEmployment(employment: Employment, updateRows = false): Promise<PayrollDetails> {
    this.loaders[this.calc.id + "-employment-" + updateRows] = true;
    return this.employmentsApi.save(employment).then(() => {
      if (this["$employment"]) {
        this["$employment"].reload();
      }
      return this.calcsApi.updateFromEmployment(this.calc, false, updateRows).then((calc) => {
        if (this.calc) {
          angular.copy(calc, this.calc);
          angular.copy(calc, this.originalCalc);
        }
        else {
          this.calc = calc;
          this.originalCalc = angular.copy(calc);
        }
        return this.saveCalc(this.calc).then(() => {
          delete this.loaders[this.calc.id + "-employment-" + updateRows];
          return this.current;
        });
      });
    });
  }

  private _calcs: CalculationListItem[] | undefined;

  /**
   * Gets the list items of the calculations of the current payroll.
   * HACK: This is temporary dev version => Need to consider different apprroaches for the final version.
   */
  public get calcs(): CalculationListItem[] | undefined {
    if (!this.current?.id) {
      return undefined;
    }
    if (!this._calcs) {
      this._calcs = [];
      this._pages = { currentIdx: 0, pages: [] };
      this.payrollsApi.getCalcsOData(this.current.id, {}).then((result) => {
        this._calcs = this.orderCalcs(this.current.input.calculations, result.items);
        this.setPageItems();
        this.getEmployments();
      });
    }
    return this._calcs;
  }

  /**
   * Filters the rows based on column header selection.
   * @param column The name of the column in question.
   * @param value Filtering value string.
   */
  public setColumnFilter(column: string, value?: string): void {
    let filter = "";

    if (column == "PartnerMessageClosed" || column == "PartnerMessageIssue") {
      const filters = [];
      this.columnFilters[column] = value;

      for (const [key, value] of Object.entries(this.columnFilters)) {
        if (value == "true") {
          filters.push(`flags/any(f: f eq '${key}')`);
        } else if (value == "false") {
          filters.push(`not flags/any(f: f eq '${key}')`);
        }
      }

      filter = filters.join(" and ");
    }

    this.payrollsApi.getCalcsOData(this.current.id, {
      $filter: filter
    }).then((result) => {
      this._calcs = this.orderCalcs(this.current.input.calculations, result.items);
      this.setPageItems();
      this.getEmployments();
    });
  }

  /** Fetches and returns all employment list items related to all calcs in payroll. */
  public getEmployments(): void {
    if (this.calcs) {
      const calcsEmploymentIds = this.calcs.map((x) => x.data.employmentId).filter((x) => !!x);

      if (calcsEmploymentIds.length > 0) {
          this.employments = Object.fromEntries(calcsEmploymentIds.map((id) => [id, undefined]));
          const chunkSize = 15; // maximum possible without maxNodeCount error
          const calls: Promise<ODataResult<any>>[] = [];
          for (let i = 0; i < calcsEmploymentIds.length; i += chunkSize) {
            const callIds = calcsEmploymentIds.slice(i, i + chunkSize);
            calls.push(this.employmentsApi.getOData({ $filter: callIds.map((id) => `id eq '${id}'`).join(" or ") }));
          }

          Promise.all(calls).then((results) => {
            results.forEach((result) => {
              result.items.forEach((item) => {
                this.employments[item.id] = item;
              });
            });
            this.$timeout();
          });
      }
    }
  }

  private _pages: { currentIdx: number, pages: Array<{ current: number, total: number, items: CalculationListItem[], calcs: Calculation[], originalCalcs: Calculation[], calcRange: string, totalCalcCount }> };
 /** Returns current calc page. */
  public get page(): { current: number, total: number, items: CalculationListItem[], calcs: Calculation[], originalCalcs: Calculation[], calcRange: string, totalCalcCount: number } | undefined {
    if (this._pages) {
      for (let i = 0; i < this._pages.pages.length; i++) {
        if (i !== this._pages.currentIdx && this._pages.pages[i].calcs.length > 0) {
          this._pages.pages[i].calcs = [];
          this._pages.pages[i].originalCalcs = [];
        }
      }
    }
    return this._pages?.pages[this._pages.currentIdx];
  }

  /** Set page items. */
  public setPageItems() {
    if (this._pages) {
      this._pages.currentIdx = 0;
      this._pages.pages.splice(0, this._pages.pages.length);
      const totalCalcCount = this._calcs.length;
      const pageTotal = Math.ceil(this._calcs.length / this.pageSize);
      for (let i = 0; i < this._calcs.length; i += this.pageSize) {
        const items = this._calcs.slice(i, i + this.pageSize);
        const calcRange = (i + 1) + " - " + (i + items.length);
        this._pages.pages.push({ current: Math.ceil((i + 1) / this.pageSize), total: pageTotal, items, calcs: [], originalCalcs: [], calcRange, totalCalcCount });
      }
    }
  }

  /** Returns current calc page. */
  public getPageCalc(item: CalculationListItem): Calculation | undefined {
    if (this._pages) {
      const currentPage = this.page;
      currentPage.calcs ??= [];
      currentPage.originalCalcs ??= [];
      const calc = currentPage.calcs.find((x) => x.id == item.id);
      if (calc) {
        if (calc.rows) {
          // ready
          return calc;
        }
        else {
          // precalc
          return undefined;
        }
      }
      else {
        const preCalc = { id: item.id };
        const preOriginalCalc = angular.copy(preCalc);
        currentPage.calcs.push(preCalc);
        currentPage.originalCalcs.push(preOriginalCalc);
        this.calcsApi.getSingle(item.id).then((calc) => {
          angular.copy(calc, preCalc);
          angular.copy(calc, preOriginalCalc);
        });
      }
    }
    else {
      return undefined;
    }
  }

  /**
   * Saves the calculation and reloads the payroll.
   */
  public savePageCalcs(): Promise<PayrollDetails> {
    const currentPage = this.page;
    this.loaders["pageCalcs" + currentPage.current] = true;
    // no precalcs, no readonly
    const calcs = this.page.calcs.filter((x) => !!x.rows && x.isReadOnly !== true);
    const changedCalcs = calcs.filter((x) => !angular.equals(x, currentPage.originalCalcs.find((c) => c.id === x.id)));
    return this.payrollsApi.saveCalculations(this.current.id, changedCalcs).then((items) => {
      for (const item of items) {
        const listItem = this.calcs.find((c) => c.id === item.id);
        if (listItem) {
          angular.copy(item, listItem);
        }
      }
      delete this.loaders["pageCalcs" + currentPage.current];
      // start reload calculations
      currentPage.calcs = currentPage.calcs.filter((x) => !changedCalcs.find((c) => c.id === x.id));
      currentPage.originalCalcs = currentPage.originalCalcs.filter((x) => !changedCalcs.find((c) => c.id === x.id));
      // and payroll
      return this.reload();
    });
  }

  /**
   * Checks if the current page calcs have changes.
   * Returns the number of changed calcs.
   */
  public get numOfChangedPageCalcs(): number {
    const currentPage = this.page;
    if (!currentPage) {
      return 0;
    }
    // no precalcs, no readonly
    const calcs = this.page.calcs.filter((x) => !!x.rows && x.isReadOnly !== true);

    return calcs.filter((x) => !angular.equals(x, currentPage.originalCalcs.find((c) => c.id === x.id))).length;
  }

  /** Gets calculation validity from the calc table entry */
  public getCalcValidity(row: CalculationListItem): ApiListItemValidation {
    return this.current?.validations?.find((x) => x.id === row.id);
  }

  /**
   * Advances to next page.
   */
  public goToPage(previous = false) {
    if (!this._pages) {
      return;
    }

    const saveIfChanged = () => {
      if (this.numOfChangedPageCalcs > 0) {
        return this.savePageCalcs();
      }
      else {
        return Promise.resolve(this.current);
      }
    };

    return saveIfChanged().then(() => {
      let idx = this._pages.currentIdx;
      if (previous) {
        idx -= 1;
        this._pages.currentIdx = idx > -1 ? idx : this._pages.pages.length - 1;
      }
      else {
        idx += 1;
        this._pages.currentIdx = idx < this._pages.pages.length ? idx : 0;
      }
    });
  }

  /**
   * Opens a list of workers in to a dialog window.
   * Creates a new calculation for the selected worker and current payroll.
   */
  public addEmployments() {
    const workerList = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/WorkerList.html",
      workerList,
      {
        title: "SALAXY.NG1.PayrollComponent.createNewCalcDialog",
      }).then((result) => {
        if (result.action === "ok" && workerList.length > 0) {
          const data: any = {};
          data.listItemType = "EmploymentListItem";
          const columns = undefined;
          const paramsTemplate = undefined;
          const action = (items, /*data*/) => {
            const itemIds = items.map((x) => x.id);
            return this.payrollsApi.addEmployments(this.current.id, itemIds).then((result) => {
              const results = items.map(() => ({ message: "", result: [] }));
              if (results.length > 0) {
                results[0].result = result;
              }
              return results;
            });
          };
          return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.createNewCalcLoading"), workerList, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
            if (dialogResult.done) {
              dialogResult.results.forEach((result) => {
                for (const item of result.result) {
                  const listItem = this.calcs.find((c) => c.id === item.id);
                  if (listItem) {
                    angular.copy(item, listItem);
                  }
                  else {
                    this.calcs.push(item);
                  }
                }
              });
              this.setPageItems();
            }
            return this.reload();
          });
        }
      });
  }

  /**
   * Opens the list of calculations into a dialog window for selection and then adds them to payroll
   * @param category Either "paid": Read-only, a copy is added ... or "draft": Editable, moved with status changed.
   * @param isMerge If true, the operation merges the calculations to the current payroll.
   * @param removeAllRows If true, the merge operation removes all the rows from the calculation.
   * @param applyEmploymentDefaultRows If true, the merge operation adds the employment default rows to the calculations.
   */
  public addCalcs(category: "paid" | "shared" | "draft" = "draft", isMerge = false, removeAllRows = false, applyEmploymentDefaultRows = false) {
    const title = (category === "paid" ? "SALAXY.NG1.PayrollComponent.addExistingCalcCopy" : category == "shared" ? "SALAXY.NG1.PayrollComponent.addExistingCalcMoveShared" : "SALAXY.NG1.PayrollComponent.addExistingCalcMove");
    this.uiHelpers.openSelectCalcs(category, title).then((result) => {
      if (result.action === "ok" && result.item.length > 0) {

        const calcs = result.item;
        const data: any = {};
        data.listItemType = "CalculationListItem";
        const columns = undefined;
        const paramsTemplate = undefined;
        const action = (items, /*data*/) => {
          const itemIds = items.map((x) => x.id);
          return (!isMerge ? this.payrollsApi.addCalculations(this.current.id, itemIds) :
            this.payrollsApi.mergeCalculations(this.current.id, itemIds, removeAllRows, applyEmploymentDefaultRows)).then((result) => {
              const results = items.map(() => ({ message: "", result: [] }));
              if (results.length > 0) {
                results[0].result = result;
              }
              return results;
            });
        };
        return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.addExistingCalcLoading"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
          if (dialogResult.done) {
            dialogResult.results.forEach((result) => {
              for (const item of result.result) {
                const listItem = this.calcs.find((c) => c.id === item.id);
                if (listItem) {
                  angular.copy(item, listItem);
                }
                else {
                  this.calcs.push(item);
                }
              }
            });
            this.setPageItems();
          }
          return this.reload();
        });
      }
    });
  }

  /**
   * Deletes the calculation
   * @param unlink If true, will not delete the actual calculation, but instead unlinks it as a separate calculation outside this payroll.
   */
  public deleteCalc(unlink = false) {
    const calcId = this.calcId;
    if (unlink) {
      this.uiHelpers.showConfirm("SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.heading",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.intro",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.confirmButton",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.cancelButton")
        .then((result: boolean) => {
          if (result) {
            const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.loading");
            this.payrollsApi.unlinkCalculation(this.current.id, calcId).then((savedPayroll) => {
              this.setCurrentValue(savedPayroll, true);
              if (this.current.input.calculations.indexOf(calcId) == -1) {
                // remove list item
                const listItem = this.calcs.find((c) => c.id === calcId);
                if (listItem) {
                  this.calcs.splice(this.calcs.indexOf(listItem), 1);
                  this.setPageItems();
                }
                this.setCalc(undefined);
              }
              loader.dismiss();
            });
          }
          else {
            // cancel
            return;
          }
        });
    } else {
      this.uiHelpers.showConfirm(
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.heading",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.introNoChanges",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.confirmButton",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.cancelButton")
        .then((result: boolean) => {
          if (result) {
            const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.loading");
            this.payrollsApi.deleteCalculation(this.current.id, calcId)
              .then((savedPayroll) => {
                this.setCurrentValue(savedPayroll, true);
                if (this.current.input.calculations.indexOf(calcId) == -1) {
                  // remove list item
                  const listItem = this.calcs.find((c) => c.id === calcId);
                  if (listItem) {
                    this.calcs.splice(this.calcs.indexOf(listItem), 1);
                    this.setPageItems();
                  }
                  this.setCalc(undefined);
                }
                loader.dismiss();
              });
          }
          else {
            return;
          }
        });
    }
  }

  /**
   * Opens a list of payrolls for merging the payroll calculations to the current payroll.
   * @param removeAllRows If true, the operation removes all the rows from the calculation.
   * @param applyEmploymentDefaultRows If true, the operation applies the employment default rows to the new calculations.
   */
  public mergePayroll(removeAllRows = false, applyEmploymentDefaultRows = false): Promise<PayrollDetails> {
    const payrollItems = [];
    return this.uiHelpers.openEditDialog("salaxy-components/modals/payroll/PayrollDetailsList.html", payrollItems, {
      title: Translations.get("SALAXY.NG1.PayrollComponent.mergePayroll.title"),
      category: "draft",
    }, "lg").then((result) => {

      if (result.action != "ok" || payrollItems.length < 1) {
        return this.current;
      }

      // get all calcs
      const calcs = [];
      let calcPromises = undefined;
      for (const payrollItem of payrollItems) {
        if (payrollItem.id == this.current.id) {
          continue;
        }
        const calcPromise = this.payrollsApi.getSingle(payrollItem.id)
          .then((payroll) => this.payrollsApi.getCalcsOData(payrollItem.id, null)
            .then((result) => calcs.push(...this.orderCalcs(payroll.input.calculations, result.items))));
        calcPromises = !calcPromises ? calcPromise : calcPromises.then(calcPromise);
      }

      return calcPromises.then(() => {
        const data: any = {};
        data.listItemType = "CalculationListItem";
        const columns = undefined;
        const paramsTemplate = undefined;
        const action = (items) => {
          const itemIds = items.map((x) => x.id);
          return this.payrollsApi.mergeCalculations(this.current.id, itemIds, removeAllRows, applyEmploymentDefaultRows).then((result) => {
            const results = items.map(() => ({ message: "", result: [] }));
            if (results.length > 0) {
              results[0].result = result;
            }
            return results;
          });
        };
        return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.mergePayroll.batchProcessTitle"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
          if (dialogResult.done) {
            dialogResult.results.forEach((result) => {
              for (const item of result.result) {
                const listItem = this.calcs.find((c) => c.id === item.id);
                if (listItem) {
                  angular.copy(item, listItem);
                }
                else {
                  this.calcs.push(item);
                }
              }
            });
            this.setPageItems();
          }
          return this.reload();
        });
      });
    });
  }

  /**
   * Shows a confirm dialog if payroll has any calculations.
   * Returns true if a user clicks OK -button. Otherwise (cancel or close) returns false.
   * @returns True if the user confirmed the operation.
   */
  public importEmploymentDataAll(): Promise<boolean> {
    const payroll = this.current;
    if (!payroll.info.calcCount || payroll.info.calcCount === 0) {
      return this.uiHelpers.showAlert("SALAXY.NG1.PayrollComponent.importEmploymentDataAllAlertTitle", "SALAXY.NG1.PayrollComponent.importEmploymentDataAllAlertDescr").then(() => false);
    } else {
      return this.uiHelpers.showConfirm(
        "SALAXY.NG1.PayrollComponent.importEmploymentDataAllConfirmTitle",
        "SALAXY.NG1.PayrollComponent.importEmploymentDataAllConfirmDescr",
        "SALAXY.UI_Terms.continue",
        "SALAXY.UI_Terms.cancel",
      ).then((result: boolean) => {
        if (result) {
          const calcs = this.calcs.slice(0);
          const data: any = {};
          data.listItemType = "CalculationListItem";
          const columns = undefined;
          const paramsTemplate = undefined;
          const action = (item /*, data */) => {
            return this.calcsApi.getSingle(item.id).then((calc) => {
              return this.calcsApi.updateFromEmployment(calc, true, true).then(() => {
                return [{ message: "", result: [] }];
              });
            });
          };
          return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.importEmploymentDataAllLoading"), calcs, action, paramsTemplate, data, columns, null, true, true).then(() => {
            return this.reload().then(() => {
              this._calcs = null;
              return true;
            });
          });
        } else {
          return false;
        }
      });
    }
  }

  /**
   * Adds salary defaults to all calculations in the current payroll.
   * Shows a confirmation dialog before proceeding.
   * If there are no calculations in the payroll, shows an alert and returns false.
   * Otherwise shows a confirmation dialog:
   * If user confirms, processes all calculations in batches of 20:
   * 1. Saves the calculations with salary defaults via payrollsApi
   * 2. Updates the local calcs list with the returned results
   * 3. Reloads the payroll data
   * 4. Returns true on success
   * If user cancels, returns false
   * @returns Promise<boolean> - True if salary defaults were successfully added, false if cancelled or no calculations
   */
  public async addSalaryDefaultsToAllCalcs(): Promise<boolean> {
    const payroll = this.current;
    if (!payroll.info.calcCount || payroll.info.calcCount === 0) {
      return this.uiHelpers.showAlert("SALAXY.NG1.PayrollComponent.importEmploymentDataAllAlertTitle", "SALAXY.NG1.PayrollComponent.addSalaryDefaultsToAllCalcsAlertTitle").then(() => false);
    } else {
      return this.uiHelpers.showConfirm(
        "SALAXY.NG1.PayrollComponent.addSalaryDefaultsToAllCalcsTitle",
        "SALAXY.NG1.PayrollComponent.addSalaryDefaultsToAllCalcsDescr",
        "SALAXY.UI_Terms.continue",
        "SALAXY.UI_Terms.cancel",
      ).then((result: boolean) => {
        if (result) {
          const calcs = this.calcs.slice(0);
          const data: any = {};
          data.listItemType = "CalculationListItem";
          const columns = undefined;
          const paramsTemplate = undefined;
          const action = (items) => {
            const itemIds = items.map((x) => x.id);
            return this.payrollsApi.saveCalculations(this.current.id, itemIds, false, true).then((result) => {
              const results = items.map(() => ({ message: "", result: [] }));
              if (results.length > 0) {
                results[0].result = result;
              }
              return results;
            });
          };
          return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.PayrollComponent.importEmploymentDataAllLoading"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
            if (dialogResult.done) {
              dialogResult.results.forEach((result) => {
                for (const item of result.result) {
                  const listItem = this.calcs.find((c) => c.id === item.id);
                  if (listItem) {
                    angular.copy(item, listItem);
                  }
                  else {
                    this.calcs.push(item);
                  }
                }
              });
              this.setPageItems();
            }
            return this.reload().then(() => {
              this._calcs = null;
              return true;
            });
          });
        } else {
          return false;
        }
      });
    }
  }

  /**
   * Show confirm dialog and export employment data from calculation to Employment relation.
   * If the calculation has unmodified changes, the calculation is saved.
   * @returns True if the user confirmed the operation.
   */
  public exportEmploymentData(): Promise<boolean> {
    return this.uiHelpers.showConfirm(
      "SALAXY.NG1.PayrollComponent.exportEmploymentDataTitle",
      "SALAXY.NG1.PayrollComponent.exportEmploymentDataDescr",
      "SALAXY.UI_Terms.continue",
      "SALAXY.UI_Terms.cancel",
    ).then((ok) => {
      if (ok) {
        const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.exportEmploymentDataLoading");
        return this.saveCalc(this.calc).then(() => {
          return this.calcsApi.updateToEmployment(this.calc).then(() => {
            loader.dismiss();
            return true;
          });
        });
      }
      else {
        return false;
      }
    });
  }

  /** Free reports */
  public clearReports() {
    this.reportCalcs = null;
    this.reportCalcs = [];
  }

  /** Refreshses the reports for the payroll. */
  public refreshReports(): Promise<Calculation[]> {
    const payroll = this.current;
    if (payroll.info.calcCount && payroll.info.calcCount > 0) {
      const calcs = this.calcs.slice(0);
      const data: any = {};
      data.listItemType = "CalculationListItem";
      const columns = undefined;
      const paramsTemplate = undefined;
      const action = (items /*, data */) => {
        const itemIds = items.map((x) => x.id);
        return this.calcsApi.getMulti(itemIds).then((result) => {
          const results = items.map(() => ({ message: "", result: [] }));
          if (results.length > 0) {
            results[0].result = result;
          }
          return results;
        });
      };
      return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.UI_Terms.wait"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
        if (dialogResult.done) {
          this.reportCalcs.splice(0, this.reportCalcs.length);
          dialogResult.results.forEach((result) => {
            this.reportCalcs.push(...result.result);
          });
        }
        return this.reportCalcs;
      });
    }
    else {
      this.reportCalcs.splice(0, this.reportCalcs.length);
      return Promise.resolve(this.reportCalcs);
    }
  }

  /** Disable function for the salary date picker. */
  public isDisabledSalaryDate = (date): boolean => {
    switch (this.current.input.salaryDateKind) {
      case SalaryDateKind.InPast:
        return false;
      case SalaryDateKind.Default:
      default:
        return !SalaryDateLogic.isValidSalaryDate(date, null, null, true, this.current.input.paymentChannel, false, undefined, true);
    }
  };

  /** Shows the paymentd dialog / the invoices UI. */
  public showPaymentDialog() {
    this.invoicesService.showPaymentDialog(this.current);
  }

  /**
   * Cancels the payment or invoice processing for the current payroll.
   */
  public cancelInvoices(): Promise<PayrollDetails> {
    const eprWarningMessage = this.invoicesService.getEprWarningForCancellation(this.current.info.salaryDate, this.current.info.status == PayrollStatus.PaymentSucceeded);
    const eprText = eprWarningMessage ? "\n" + eprWarningMessage : "";
    return this.uiHelpers.showConfirm(Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogTitle") + "?", Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogDescr") + `${eprText}`, "SALAXY.NG1.CalcResultsComponent.cancelDialogYes", "SALAXY.NG1.CalcResultsComponent.cancelDialogNo").then((ok) => {
      if (ok) {
        if (InvoicesLogic.isCfa(this.current.input.paymentChannel)) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          return this.payrollsApi.cancelInvoices(this.current.id, [], false).then(() => {
            return this.reload().then(() => {
              loading.dismiss();
              this._calcs = null;
              return this.current;
            });
          });
        }
        else {
          const calcs = angular.copy(this.calcs);
          const data: any = {};
          data.listItemType = "CalculationListItem";
          const columns = undefined;
          const paramsTemplate = undefined;
          const action = (items, data) => {
            const itemIds = items.map((x) => x.id);
            const remainingAfterThis = data.dialogList.filter((x) => !x.processed).length;
            const excludePeriodic = remainingAfterThis == 0 ? false : true;
            return this.payrollsApi.cancelInvoices(this.current.id, itemIds, excludePeriodic).then(() => {
              const results = items.map(() => ({ message: "", result: [] }));
              return results;
            });
          };
          return this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.UI_Terms.wait"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then(() => {
            return this.reload().then(() => {
              this._calcs = null;
              return this.current;
            });
          });
        }
      }
      else {
        return this.current;
      }
    });
  }

  /**
   * Gets the user-specific setting (service), stored in the local storage.
   */
  public get userSettings() {
    return this.userSettingsService;
  }


  private _info = {
    calcCount: {
      actual: 0,
      original: 0,
      exception: false,
    },
    totalGrossSalary: {
      actual: 0,
      original: 0,
      exception: false,
    },
    totalPayment: {
      actual: 0,
      original: 0,
      exception: false,
    },
  };

  /** Get calculated header info. */
  public get info() {
    const allCalcs = this.calcs ?? [];
    const infoCalcs = allCalcs.filter((x) => {
      switch (this.current?.info.status) {
        case PayrollStatus.PaymentSucceeded:
          return x.status === CalculationStatus.PaymentSucceeded;
        case PayrollStatus.PaymentStarted:
          return x.status === CalculationStatus.PaymentStarted || x.status === CalculationStatus.PaymentSucceeded;
        default:
          return true;
      }
    });

    this._info.calcCount.original = this.current?.info.calcCount ?? 0;
    this._info.calcCount.actual = infoCalcs.length;
    this._info.calcCount.exception = this._info.calcCount.actual !== this._info.calcCount.original;

    this._info.totalGrossSalary.original = Numeric.round(this.current?.info.totalGrossSalary ?? 0);
    this._info.totalGrossSalary.actual = Numeric.round(Arrays.sum(infoCalcs, (x) => x.grossSalary ?? 0));
    this._info.totalGrossSalary.exception = this._info.totalGrossSalary.actual !== this._info.totalGrossSalary.original;

    this._info.totalPayment.original = Numeric.round(this.current?.info.totalPayment ?? 0);
    this._info.totalPayment.actual = Numeric.round(Arrays.sum(infoCalcs, (x) => x.payment ?? 0));
    this._info.totalPayment.exception = this._info.totalPayment.actual !== this._info.totalPayment.original;

    return this._info;
  }

  /** Returns exception style. */
  public getExceptionStyle(hasException: boolean) {
    if (hasException) {
      return { "text-decoration": "line-through" };
    }
    return {};
  }

  /** Returns true, if a warning should be indicated in a row. */
  public rowStatusWarning(row: CalculationListItem): boolean {
    switch (this.current?.info.status) {
      case PayrollStatus.PaymentSucceeded:
        return !(row.status === CalculationStatus.PaymentSucceeded);
      case PayrollStatus.PaymentStarted:
        return !(row.status === CalculationStatus.PaymentStarted || row.status === CalculationStatus.PaymentSucceeded);
      default:
        return false;
    }
  }

  /** Copy current calc */
  public copyCalc() {
    const copy = angular.copy(this.calc);
    copy.id = null;
    copy.info.payrollId = null;
    copy.workflow.status = CalculationStatus.Draft;
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    return this.calcsApi.save(copy).then(() => {
      loading.dismiss();
    });
  }

  /** Returns true if the curren payroll has cfa payment channel. */
  public get isCfa(): boolean {
    return InvoicesLogic.isCfa(this.current?.input?.paymentChannel);
  }

  /** Pay current calc */
  public payCalc() {
    if (!this.isCfa) {
      this.invoicesService.showPaymentDialog(this.calc);
    }
  }

  /**
   * Sends a payroll with messaging system and sets
   * @param $msg The MessageThreadCrudController that is used for creating the message thread.
   */
  public sendPayrollMessage($msg: MessageThreadCrudController): Promise<void> {
    if (!$msg.current) {
      $msg.model = "new";
    }
    const model = $msg.current;
    model.otherParty = this.sessionService.session.settings.partner.accountId;
    model.status = MessageThreadStatus.Active;
    model.title = model.title || "Palkkalista " + this.current.input.title;
    model.ownerWorkflow = {
      salaryDate: this.current.info.salaryDate,
    };
    model.messages.push({
      contentId: this.currentId,
      fromParty: this.sessionService.isInRole("pro") ? MessageFrom.OtherParty : MessageFrom.Owner,
      type: ThreadedMessageType.Payroll
    });
    return $msg.save().then((msg) => {
      if (this.current.info.status != PayrollStatus.WaitingApproval) {
        this.current.info.messageThreadId = msg.id;
        this.current.info.status = PayrollStatus.WaitingApproval;
        return this.save().then(() => {
          return;
        });
      } else {
        this.current.info.messageThreadId = msg.id; // This is done by messageThread logic on server => ne need to save.
      }
    });
  }

  /**
   * Opens the workflow for the payroll.
   * @param $msg The MessageThreadCrudController that is used for creating the message thread.
   */
  public openWorkflowForpayroll($msg: MessageThreadCrudController): Promise<void> {
    if (!$msg.current) {
      $msg.model = "new";
    }
    const model = $msg.current;
    model.otherParty = this.sessionService.session.settings.partner.accountId;
    model.status = MessageThreadStatus.Active;
    model.title = model.title || "Palkkalista " + this.current.input.title;
    model.ownerWorkflow = {
      salaryDate: this.current.info.salaryDate,
    };
    model.messages.push({
      contentId: this.currentId,
      fromParty: this.sessionService.isInRole("pro") ? MessageFrom.OtherParty : MessageFrom.Owner,
      type: ThreadedMessageType.Payroll
    });
    return $msg.save().then((msg) => {
      this.current.info.messageThreadId = msg.id;
        return this.save().then(() => {
          return;
        });
    });
  }

  /**
   * Gets the message title for the action.
   */
  public getMessageTitle(action: string) {
    return this.messageActions.find((x) => x.value == action).title + ": " + this.current.input.title;
  }

  /**
   * Available actions for the message.
   * TODO: This could be replaced by enums or it may come from Service model.
   */
  public messageActions: InputEnumOption[] = [
    {
      text: "Tarkista",
      value: "check",
      title: "Tarkista ennen maksuun lähetystä palkkalista",
    },
    {
      text: "Maksa",
      value: "pay",
      title: "Maksa palkkalista",
    },/* {
      text: "Tarkista (hyväksy/hylkää)",
      value: "approve",
      title: "Tarkista palkkalista",
    },*/
    {
      text: "Tiedoksi",
      value: "info",
      title: "Tiedoksi palkkalista",
    }, {
      text: "Vastaa kysymykseen",
      value: "question",
      title: "Kysymyksiä palkkalistasta",
    }
  ];

  /**
   * Deletes a linked message: Both the message thread and msg id in this payroll.
   * @param $msg The MessageThreadCrudController that contains the current message.
   */
  public deleteMessage($msg: MessageThreadCrudController) {
    this.uiHelpers.showConfirm("Haluatko poistaa viestiketjun?", "Haluatko varmasti poistaa viestiketjun?")
      .then((result) => {
        if (result) {
          if ($msg.current) {
            $msg.deleteNoConfirm($msg.current).then(() => {
              this.current.info.messageThreadId = null;
              this.save();
            });
          } else {
            this.current.info.messageThreadId = null;
            this.save();
          }
        }
      });
  }

  /** Show add new taxcard dialog */
  public async showAddNewTaxcard($event: angular.IAngularEvent, item: CalculationListItem) {
    $event.stopPropagation();
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");

    const calc = this.getPageCalc(item) ?? await this.calcsApi.getSingle(item.id);

    if (!calc || calc.isReadOnly) {
      loading.dismiss();
      return;
    }
    const empl = await this.employmentsApi.getSingle(calc.worker.employmentId);
    if (!empl) {
      loading.dismiss();
      return;
    }

    if (empl.info.taxcard == EmploymentRelationTaxcard.Undefined || empl.info.taxcard == EmploymentRelationTaxcard.Auto) {
      const taxcard = await this.taxcardsApi.setAuto(calc.worker.paymentData.socialSecurityNumberValid);
      if (taxcard) {
        await this.saveCalc(calc);
        loading.dismiss();
        return;
      }
    }

    const result = await this.uiCrudHelpers.createNewTaxcard(calc.worker.paymentData.socialSecurityNumberValid);
    if (result.action === "ok") {
      await this.saveCalc(calc);
      loading.dismiss();
      return;
    }

    loading.dismiss();
  }


  /** Sets the ordering for the rows. */
  public orderBy(field: string) {
    if (this.orderField == field) {
      if (this.orderReverse) {
        // reset to original
        this.orderField = null;
        this.orderReverse = false;
      }
      else {
        // flip
        this.orderReverse = true;
      }
    }
    else {
      this.orderField = field;
      this.orderReverse = false;
    }

    if (!this.orderField) {
      this.orderCalcs(this.current.input.calculations, this.calcs);
    }
    else {
      this.calcs.sort(this.getOrderBy());
    }

    this.setPageItems();
  }

  public rowsImportParams = {
    title: "",
    personalId: "",
    employmentType: "",
    payrollId: "",
    period: {
      start: "",
      end: "",
      daysCount: 0,
    },
  };

  /** Initializes the UI parameters for the rows import data. */
  public initRowsImportParams() {
    this.rowsImportParams.title = `Demo data from calculation ${this.calc.worker.avatar.sortableName} ${this.calc.workflow.salaryDate || "No date"}`;
    this.rowsImportParams.personalId = this.calc.worker.paymentData.socialSecurityNumberValid;
    this.rowsImportParams.employmentType = this.calc.result.responsibilities.employmentType;
    this.rowsImportParams.payrollId = this.current.id;
    this.rowsImportParams.period.start = this.calc.info.workStartDate;
    this.rowsImportParams.period.end = this.calc.info.workEndDate;
    this.rowsImportParams.period.daysCount = this.calc.framework.numberOfDays;
  }

  /** Copies the rows import data to the clipboard. */
  public copyRowsImportData() {
    if (!this.calc?.rows?.length) {
      alert("Ei rivejä valitussa laskelmassa.");
      return;
    }

    const period = {
      start: this.rowsImportParams.period.start,
      end: this.rowsImportParams.period.end,
    };
    let counter = 0;
    const data: WorktimeImport = {
      header: {
        title: this.rowsImportParams.title,
        period,
      },
      rows: this.calc.rows.map((x) => {
        const row: WorktimeImportRow = {
          employmentType: this.rowsImportParams.employmentType as EmploymentRelationType,
          personalId: this.rowsImportParams.personalId,
          rowType: x.rowType,
          type: x.type,
          count: x.count,
          message: x.message,
          period: x.period || {},
          accounting: x.accounting,
          sourceId: "DemoImport" + counter++,
          // Not typically used in import data.
          // price: x.price,
          // unit: x.unit,
          data: {
          }
        };
        row.period.start = row.period?.start || period.start;
        row.period.end = row.period?.end || period.end;
        const data = structuredClone(x.data);
        delete data.payrollPeriodId;
        delete data.importExplicitPrice;
        delete data.committedAt;
        delete data.status;
        data.isApproved = true;
        data.payrollId = this.rowsImportParams.payrollId;
        row.data = data;
        return row;
      }),
    };
    const json = JSON.stringify(data, null, 2);
    window.navigator.clipboard.writeText(json);

    alert("Rivit on kopioitu leikepöydälle");
  }

  /** Ordering filter */
  private getOrderBy(): (a: CalculationListItem, b: CalculationListItem) => number | null {
    switch (this.orderField) {
      case "status":
        return (r1, r2) => {
          const status1 = `${Translations.get("SALAXY.ENUM.CalculationStatus." + (Objects.getProperty(r1, this.orderField) ?? "") + ".label")}`;
          const status2 = `${Translations.get("SALAXY.ENUM.CalculationStatus." + (Objects.getProperty(r2, this.orderField) ?? "") + ".label")}`;
          return (this.orderReverse ? -1 : 1) * status1.localeCompare(status2, "fi");
        };
        break;
      // numbers
      case "grossSalary":
      case "data.workerPayment":
        return (r1, r2) => {
          return (this.orderReverse ? -1 : 1) * ((Objects.getProperty(r1, this.orderField) ?? 0) - (Objects.getProperty(r2, this.orderField) ?? 0));
        };
      // strings
      default:
        return (r1, r2) => {
          return (this.orderReverse ? -1 : 1) * (Objects.getProperty(r1, this.orderField) ?? "").localeCompare(Objects.getProperty(r2, this.orderField) ?? "", "fi");
        };
    }
  }

  private getHeaderChangeInfo(): HeaderChangeInfo {
    return {
      isPaymentChannelChanged: this.current.input.paymentChannel !== this.original.input.paymentChannel,
      isPeriodChanged: (this.current.input.period.daysCount !== this.original.input.period.daysCount ||
        this.current.input.period.start !== this.original.input.period.start ||
        this.current.input.period.end !== this.original.input.period.end),
      isSalaryDateChanged: this.current.input.salaryDate !== this.original.input.salaryDate || this.current.input.salaryDateKind !== this.original.input.salaryDateKind,
    };
  }

  private orderCalcs(ids: string[], items: CalculationListItem[]): CalculationListItem[] {
    if (!items) {
      return items;
    }
    items.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
    return items;
  }
}
